import { headerAuth } from "../standardAuthPatterns";
import { defineConnector } from "../types";
import { exactUrlToRegex } from "../urlPatternConversion";
import logo from "./logo.svg";

export const exterroFtk = defineConnector({
  identifier: "exterroFtk",
  name: "Exterro FTK",
  tagLine: "Digital forensics",
  logo,
  protocol: "http",
  tags: ["security"],
  defaultUrlPattern: ({ baseUrl }) =>
    exactUrlToRegex(
      baseUrl || "https://ftk.example.com:4443/api/v2/enterpriseapi"
    ),
  auth: headerAuth([
    { name: "EnterpriseAPIKey", value: { from: "secret", field: "apiKey" } },
  ]),
  steps: [
    {
      title: "Set up Exterro FTK",
      items: [
        {
          kind: "markdown",
          content:
            "1. Confirm that the FTK Central or FTK Enterprise deployment is reachable over HTTPS and obtain its host and port. The documented API prefix is `/api/v2/enterpriseapi`.\n2. Ensure a CodeMeter license dongle with the **ADAPI** license tag is available to FTK. In FTK Enterprise, select **Tools > Access API Key**, choose a user with application administrator rights, and select **Generate Key**. The official guide recommends the Administrator account when access to all endpoints is needed; the key inherits the selected user’s permissions.\n3. Store the generated key as the `EnterpriseAPIKey` request-header credential. The guide states that API keys do not expire after a defined period.\n\nReferences: [FTK 8.1 downloads and documentation](https://www.exterro.com/ftk-downloads/forensic-tools-8-1), [FTK Central Enterprise API Master Guide](https://d1kpmuwb7gvu1i.cloudfront.net/8.x/8.1.0/Exterro%20FTK%20Central%20-%20Enterprise%20API%20Master%20Guide.pdf), and [FTK Central](https://www.exterro.com/digital-forensics-software/ftk-central).",
        },
      ],
    },
    {
      title: "Enter connection details",
      items: [
        {
          kind: "url",
          label: "API base URL",
          required: true,
          placeholder: "https://ftk.example.com:4443/api/v2/enterpriseapi",
          target: {
            kind: "configuration",
            field: "baseUrl",
          },
          helperText:
            "The official guide does not publish a universal production URL. Replace the documented self-hosted example host https://localhost:4443 with the HTTPS host and port of the customer’s FTK Central or FTK Enterprise deployment, while retaining /api/v2/enterpriseapi as the API path prefix.",
        },
        {
          kind: "input",
          label: "Enterprise API key",
          required: true,
          target: {
            kind: "secrets",
            field: "apiKey",
          },
          helperText:
            "Generate an API key from FTK Enterprise through Tools > Access API Key, or through the documented FTK Central API-key flow. The generating user must have application administrator rights; the key inherits that user’s permissions.",
        },
      ],
    },
  ],
  connectionTest: {
    protocol: "http",
    request: ({ baseUrl }) => ({
      url:
        (baseUrl || "https://ftk.example.com:4443/api/v2/enterpriseapi")
          .trim()
          .replace(/\/+$/, "") + "/jobs/states",
      ...{ method: "GET" },
    }),
  },
});
